export type TimezoneOption = { value: string; label: string };

/** e.g. "America/Toronto" -> "UTC-04:00" (falls back to "" if the runtime can't resolve it). */
function getUtcOffsetLabel(timeZone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      timeZoneName: "shortOffset",
    }).formatToParts(at);
    const offset = parts.find(part => part.type === "timeZoneName")?.value ?? "";
    return offset.replace("GMT", "UTC");
  } catch {
    return "";
  }
}

/**
 * Major Canadian cities don't get their own IANA zone (there's no
 * "America/Ottawa" or "America/Calgary") — they share the nearest canonical
 * zone for their province/territory. Listed here so searching "Ottawa" or
 * "Calgary" surfaces the right entry instead of only matching the raw IANA
 * identifier of the zone's namesake city.
 */
const CANADIAN_CITY_ALIASES: Record<string, string[]> = {
  "America/St_Johns": ["St. John's"],
  "America/Goose_Bay": ["Happy Valley-Goose Bay"],
  "America/Halifax": ["Halifax", "Sydney", "Charlottetown", "Saint John"],
  "America/Moncton": ["Moncton", "Fredericton"],
  "America/Toronto": [
    "Toronto",
    "Ottawa",
    "Montreal",
    "Quebec City",
    "Mississauga",
    "Hamilton",
    "London",
    "Windsor",
    "Kingston",
    "Sudbury",
  ],
  "America/Atikokan": ["Atikokan", "Thunder Bay"],
  "America/Winnipeg": ["Winnipeg", "Brandon"],
  "America/Regina": ["Regina", "Saskatoon", "Moose Jaw"],
  "America/Swift_Current": ["Swift Current"],
  "America/Edmonton": ["Edmonton", "Calgary", "Red Deer", "Lethbridge"],
  "America/Vancouver": [
    "Vancouver",
    "Victoria",
    "Surrey",
    "Kelowna",
    "Kamloops",
    "Prince George",
  ],
  "America/Fort_Nelson": ["Fort Nelson"],
  "America/Whitehorse": ["Whitehorse"],
  "America/Yellowknife": ["Yellowknife"],
  "America/Inuvik": ["Inuvik"],
  "America/Iqaluit": ["Iqaluit"],
  "America/Rankin_Inlet": ["Rankin Inlet"],
  "America/Cambridge_Bay": ["Cambridge Bay"],
};

/**
 * Every IANA timezone identifier supported by the current runtime (~400),
 * sorted alphabetically by identifier, each paired with a friendly label
 * showing today's UTC offset (e.g. "America/Toronto (UTC-04:00)"). `value`
 * is always the raw IANA identifier — that's what gets sent to the server.
 * Zones covering a major Canadian city (see `CANADIAN_CITY_ALIASES`) list
 * those city names in the label too, so they're findable by search even
 * though they don't have their own IANA identifier.
 */
export const TIMEZONE_OPTIONS: TimezoneOption[] = (
  typeof Intl.supportedValuesOf === "function"
    ? Intl.supportedValuesOf("timeZone")
    : []
)
  .map(timeZone => {
    const cities = CANADIAN_CITY_ALIASES[timeZone];
    const namePart = timeZone.replace(/_/g, " ");
    const label = cities
      ? `${namePart} — ${cities.join(", ")} (${getUtcOffsetLabel(timeZone)})`
      : `${namePart} (${getUtcOffsetLabel(timeZone)})`;
    return { value: timeZone, label };
  })
  .sort((a, b) => a.value.localeCompare(b.value));
